import { Api } from '../../core/Api';
import { ErrorCode, PrimerClientError } from '../../errors';
import { AmountChange, BillingAddress, OrderItem } from '../../types';
import { parseAmountValue } from '../../utils/parseAmountValue';
import {
  TaxCalculationConstructorOptions,
  TaxCalculationInput,
  TaxCalculationOptions,
  TaxResult,
} from './types';

export const createTaxCalculationInput = (
  options: TaxCalculationOptions,
): TaxCalculationInput => {
  const nexusAddresses = options.businessDetails?.nexusAddresses?.map(
    (nexusAddress: BillingAddress) => {
      return {
        country: nexusAddress.countryCode,
        state: nexusAddress.state,
        zip: nexusAddress.postalCode,
        city: nexusAddress.city,
        street: nexusAddress.addressLine1,
      };
    },
  );

  const lineItems = options.orderDetails?.items?.map((lineItem: OrderItem) => {
    return {
      id: lineItem.name,
      quantity: lineItem.quantity,
      productTaxCode: lineItem.taxCode,
      unitPrice: lineItem.unitAmount
        ? parseAmountValue(lineItem.unitAmount).asNumber()
        : 0,
      discount: lineItem.discountAmount
        ? parseAmountValue(lineItem.discountAmount).asNumber()
        : 0,
    };
  });

  const totalAmount =
    options.orderDetails.totalAmount &&
    (!options.orderDetails.items || options.orderDetails.items?.length < 1)
      ? parseAmountValue(options.orderDetails.totalAmount).asNumber() -
        parseAmountValue(options.orderDetails.shippingAmount ?? 0).asNumber() -
        parseAmountValue(options.orderDetails.totalTaxAmount ?? 0).asNumber()
      : undefined;

  return {
    customerId: options.customerDetails?.customerTaxId,
    toAddress: {
      toCountry: options.customerDetails?.shippingAddress?.countryCode,
      toState: options.customerDetails?.shippingAddress?.state,
      toZip: options.customerDetails?.shippingAddress?.postalCode,
      toCity: options.customerDetails?.shippingAddress?.city,
      toStreet: options.customerDetails.shippingAddress?.addressLine1,
    },
    fromAddress: {
      fromCountry: options.businessDetails?.address?.countryCode,
      fromState: options.businessDetails?.address?.state,
      fromZip: options.businessDetails?.address?.postalCode,
      fromCity: options.businessDetails?.address?.city,
      fromStreet: options.businessDetails?.address?.addressLine1,
    },
    shipping: options.orderDetails?.shippingAmount
      ? parseAmountValue(options.orderDetails?.shippingAmount).asNumber()
      : 0,
    amount: totalAmount,
    nexusAddresses,
    lineItems: lineItems && lineItems.length > 0 ? lineItems : undefined,
  };
};

export const createMerchantTaxData = (taxResult: TaxResult): AmountChange => {
  return {
    reason: 'TAX',
    totalTaxAmount: taxResult.salesTax,
    totalAmount: taxResult.totalAmount + taxResult.salesTax,
  };
};

export default class TaxCalculation {
  private api: Api;

  private requestUrl: string;

  constructor(options: TaxCalculationConstructorOptions) {
    this.api = options.api;
    this.requestUrl = options.requestUrl;
  }

  public async calculateSalesTax(
    options: TaxCalculationOptions,
  ): Promise<AmountChange> {
    try {
      const requestData = createTaxCalculationInput(options);
      const { data, error } = await this.api.post<unknown, TaxResult>(
        this.requestUrl,
        requestData,
      );
      if (!data || error) {
        throw PrimerClientError.fromErrorCode(ErrorCode.TAX_ERROR, {
          message: error?.message ?? "Can't calculate tax",
        });
      }
      return createMerchantTaxData(data);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
